import {
  Feature,
  IBaseProperties,
  IBoundary,
  ICommonList,
  ICustomProperties,
  IGeoData,
  INotSure,
  ISubCategories,
  QueryType,
} from "@/types";
import { GeoJsonObject } from "geojson";
import { appName, clientDetail } from "./Constant";
import { getValue } from "./Storage";

/**
 * @function capitalizeWord
 * @author traj3ctory
 * @param {string} word
 * @return {string}
 */

export const capitalizeWord = (word: string): string => {
  if (typeof word !== "string") {
    return "";
  }

  const words = word.split(" ");
  const capitalizedWords = words.map(
    (w) => w?.charAt(0).toUpperCase() + w?.slice(1)
  );

  return capitalizedWords?.join(" ");
};
// ============================================

/**
 * @function getPageTitle
 * @param {string} title
 * @returns {string}
 */
export const getPageTitle = (title: string = appName): string => {
  const _userData = getValue(clientDetail);
  const userData = _userData ? JSON.parse(_userData) : null;
  const _user = userData ? userData?.username?.split("@")[0] : null;
  // capitalize user
  const user = capitalizeWord(_user);
  return user
    ? `${user}'s ${title} page`
    : title
    ? `${title} | ${appName}`
    : appName;
};
// ============================================

/**
 * @function mergeClassNames
 * @param {string} name
 * @returns {string}
 */

// export const mergeClassNames = (
//   ...classes: (string | [boolean, string])[]
// ): string => {
//   return classes
//     .map((cls) => {
//       if (Array.isArray(cls)) {
//         const [condition, className] = cls;
//         return condition ? className : "";
//       }
//       return cls;
//     })
//     .filter(Boolean)
//     .join(" ");
// };

export const mergeClassNames = (
  ...classes: (string | [boolean, string])[]
): string => {
  const classMap: Record<string, boolean> = {};

  classes.forEach((cls) => {
    if (Array.isArray(cls)) {
      const [condition, className] = cls;
      classMap[className] = classMap[className] || condition; // Combine conditions for same className
    } else if (typeof cls === "string") {
      classMap[cls] = true;
    }
  });

  return Object.keys(classMap)
    .filter((className) => classMap[className])
    .join(" ");
};

/**
 * @function isStr
 * @param {string | string[]} value
 */
export const isStr = (value: string | string[]): value is string => {
  return typeof value === "string";
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: INotSure[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const maxWords = (str: string, max = 15): string => {
  const words = str.split(" ");
  if (words.length > max) {
    return `${words.slice(0, max).join(" ")}...`;
  }
  return str;
};

/**
 * @function generateBackground
 * @description generate a background color from a string
 */

export const generateBackground = (str: string) => {
  // Use a hash function to generate a color code based on the username
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Calculate color code
  const c = (hash & 0x00_ff_ff_ff).toString(16).toUpperCase();

  // Return a hex color code with a leading # and six digits
  return `#${"00000".substring(0, 6 - c.length)}${c}`;
};

/**
 * @function maxWord
 * @param {string} str
 */
export const maxWord = (str: string, max = 15) => {
  return str?.length > max ? `${str.substring(0, max)}...` : str;
};

export function isGeoJsonObject(data: INotSure): data is GeoJsonObject {
  return data && typeof data === "object" && "type" in data;
}

export function hasGeoData(obj: INotSure): obj is { geo_data: IGeoData[] } {
  return obj && Array.isArray(obj.geo_data);
}

export function hasIBaseProperties(obj: INotSure): obj is IBaseProperties {
  return (
    obj &&
    typeof obj.total_number_of_sba === "number" &&
    typeof obj.total_number_of_schews === "number" &&
    typeof obj.total_sba_gaps === "number"
  );
}

export function hasFeatures(obj: INotSure): obj is { features: Feature[] } {
  return obj && Array.isArray(obj.features);
}

export function isGeoJson(obj: INotSure): obj is { type: "geojson" } {
  return obj && obj.type === "geojson";
}

export function isCustomProperties(obj: INotSure): obj is ICustomProperties {
  return hasIBaseProperties(obj) && hasFeatures(obj);
}

export function isBoundary(data: INotSure): data is IBoundary {
  return data && data.type === "geojson";
}

/**
 * @function restructureData
 * @description restructure data to be used in the select component
 */
export const restructureData = <T extends ICommonList>(arg: T[]) => {
  return arg?.map((el: T) => ({
    label: el.name?.toLocaleUpperCase() || el?.name,
    value: el.id,
  }));
};

/**
 * @function generateApiQuery
 * @description generate a query string from an object
 * @param {Object} params
 * @returns {string}
 */

export const generateApiQuery = (
  params: Record<string, string | number | boolean>
): string => {
  const queryParams: INotSure = {}; // Create an empty object

  // Iterate through the params and add non-empty values to queryParams
  for (const key in params) {
    if (params[key]) {
      queryParams[key] = params[key];
    }
  }

  // Convert the queryParams object to a URL-encoded string
  const query = new URLSearchParams(queryParams).toString();
  return `?${query}`;
};

/**
 * @function indicatorQueryBuilder
 * @description build a query string from selected indicators
 */

export const indicatorQueryBuilder = (selectedIndicator: ISubCategories[]) => {
  const query: QueryType = {};
  const facilityType: string[] = [];
  const facilityOwner: string[] = [];
  const facilityStatus: string[] = [];
  const facilityFunding: string[] = [];

  selectedIndicator?.forEach((indicator: ISubCategories) => {
    if (indicator.is_boolean) {
      query[indicator.value] = true;
    } else if (indicator.is_boolean === false) {
      query[`${indicator.value}__gte`] = 1;
    } else {
      if (indicator.category_name === "Facility Type")
        facilityType.push(indicator.value);
      if (indicator.category_name === "Facility Ownership")
        facilityOwner.push(indicator.value);
      if (indicator.category_name === "Facility Status")
        facilityStatus.push(indicator.value);
      if (indicator.category_name === "Facility Funding")
        facilityFunding.push(indicator.value);
    }
  });

  return {
    queryString: encodeURIComponent(JSON.stringify(query)),
    facilityTypeString: encodeURIComponent(JSON.stringify(facilityType)),
    facilityOwnerString: encodeURIComponent(JSON.stringify(facilityOwner)),
    facilityStatusString: encodeURIComponent(JSON.stringify(facilityStatus)),
    facilityFundingString: encodeURIComponent(JSON.stringify(facilityFunding)),
  };
};

/**
 * @function convertObjectToQueryString
 * @description convert object to query string
 */
export const convertObjectToQueryString = (
  obj: Record<string, INotSure>
): string => {
  const jsonString = JSON.stringify(obj);
  const params = new URLSearchParams({ query: jsonString });
  return `${params.toString()}`;
};

/**
 * @function convertArrayToQueryString
 * @description convert array to query string
 */
export const convertArrayToQueryString = (fields: string[]): string => {
  const jsonString = JSON.stringify(fields);
  const params = new URLSearchParams({ fields: jsonString });
  return `${params.toString()}`;
};

export const extractDate = (dateTimeString: string): string => {
  const date = new Date(dateTimeString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
